import React, { useEffect } from 'react'
import { useFormik } from 'formik'
import * as Yup from "yup"
import { Button, Form, FormFeedback, FormGroup, Input, Label, Modal, ModalBody, ModalHeader } from 'reactstrap'
import { useDispatch, useSelector } from 'react-redux'
import { createRenewalTrigger, getAllRenewalTrigger, updateRenewalTrigger } from 'store/actions'
import { toast } from 'react-toastify'
import DatePicker from 'components/Common/DatePicker'

const TriggerForm = ({
    renewalTrackTrigger,
    setRenewalTrackTrigger
}) => {
    const dispatch = useDispatch();

    const { error, responseMsg, loading, isTriggerCreated } = useSelector(state => state.RenewalReducer);

    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString();


    const validation = useFormik({
        enableReinitialize: true,
    
        initialValues: {
            id: renewalTrackTrigger.isEdit || renewalTrackTrigger.isView ? renewalTrackTrigger?.data?.id : "",
            name: renewalTrackTrigger.isEdit || renewalTrackTrigger.isView ? renewalTrackTrigger?.data?.name : "",
            filter_mode: renewalTrackTrigger.isEdit || renewalTrackTrigger.isView ? renewalTrackTrigger?.data?.filter_mode : "",
            filter_frequency: renewalTrackTrigger.isEdit || renewalTrackTrigger.isView ? renewalTrackTrigger?.data?.filter_frequency : "",
            trigger_frequency: renewalTrackTrigger.isEdit || renewalTrackTrigger.isView ? renewalTrackTrigger?.data?.trigger_frequency : "",
            weekly_trigger_day: renewalTrackTrigger.isEdit || renewalTrackTrigger.isView ? renewalTrackTrigger?.data?.weekly_trigger_day : "",
            monthly_trigger_date: renewalTrackTrigger.isEdit || renewalTrackTrigger.isView ? renewalTrackTrigger?.data?.monthly_trigger_date : "",
            next_trigger_date: renewalTrackTrigger.isEdit || renewalTrackTrigger.isView ? renewalTrackTrigger?.data?.next_trigger_date : formattedDate,
            start_date: renewalTrackTrigger.isEdit || renewalTrackTrigger.isView ? renewalTrackTrigger?.data?.start_date : "",
            status: renewalTrackTrigger.isEdit || renewalTrackTrigger.isView ? renewalTrackTrigger?.data?.status : "1",
        },
        validationSchema: Yup.object({
            name: Yup.string().required("Please Enter Trigger Name"),
            filter_mode: Yup.string().required("Please Select Filter Mode"),
            filter_frequency: Yup.number().required("Please Select Filter Frequency"),
            trigger_frequency: Yup.string().required("Please Select Trigger Frequency"),
            start_date: Yup.date().required("Please Select Start Date"),
            status: Yup.number().required("Please Select Status"),
        }),
        onSubmit: values => {
            if (renewalTrackTrigger.isEdit) {
                const updateTriggerData = {
                    id: values.id,
                    name: values.name,
                    filter_mode: values.filter_mode,
                    filter_frequency: values.filter_frequency,
                    trigger_frequency: values.trigger_frequency,
                    weekly_trigger_day: values.weekly_trigger_day,
                    monthly_trigger_date: values.monthly_trigger_date,
                    next_trigger_date: values.next_trigger_date,
                    start_date: values.start_date,
                    status: values.status
                };
                dispatch(updateRenewalTrigger(updateTriggerData))
                console.log(updateTriggerData);
            } else {
                const newTrigger = {
                    name: values.name,
                    filter_mode: values.filter_mode,
                    filter_frequency: values.filter_frequency,
                    trigger_frequency: values.trigger_frequency,
                    weekly_trigger_day: values.weekly_trigger_day,
                    monthly_trigger_date: values.monthly_trigger_date,
                    next_trigger_date: values.next_trigger_date,
                    start_date: values.start_date,
                    status: values.status
                };
                console.log(newTrigger);
                dispatch(createRenewalTrigger(newTrigger));
            }
        },
    });

    const closeTriggerForm = () => {
        setRenewalTrackTrigger({
            ...renewalTrackTrigger,
            isShow: false,
            isEdit: false,
            isView: false,
            data: ""
        })
        validation.resetForm()
    }

    useEffect(() => {
        if (error) {
            toast.error(error)
        }
    }, [error])

    useEffect(() => {
        if (responseMsg) {
            toast.success(responseMsg)
        }
    }, [responseMsg])



    useEffect(() => {
        if (isTriggerCreated) {
            closeTriggerForm();
            dispatch(getAllRenewalTrigger())
        }
    }, [isTriggerCreated])

    return (
        <Modal isOpen={renewalTrackTrigger.isShow} toggle={closeTriggerForm}>
            <ModalHeader toggle={closeTriggerForm}>Renewal Tracking Trigger</ModalHeader>
            <ModalBody>
                <Form
                    onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                    }}
                >
                    {/* Name */}
                    <FormGroup>
                        <Label htmlFor="name">Name</Label>
                        <Input
                            id="name"
                            name="name"
                            type="text"
                            value={validation.values.name}
                            disabled={renewalTrackTrigger.isView || renewalTrackTrigger.isEdit}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={validation.touched.name && !!validation.errors.name}
                        />
                        {validation.touched.name && validation.errors.name && (
                            <FormFeedback>{validation.errors.name}</FormFeedback>
                        )}
                    </FormGroup>

                    {/* Filter Mode */}
                    <FormGroup>
                        <Label htmlFor="filter_mode">Filter Mode</Label>
                        <Input
                            type="select"
                            id="filter_mode"
                            name="filter_mode"
                            value={validation.values.filter_mode}
                            disabled={renewalTrackTrigger.isView || renewalTrackTrigger.isEdit}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={validation.touched.filter_mode && !!validation.errors.filter_mode}
                        >
                            <option value="">Select</option>
                            <option value="expire within">Expire Within</option>
                            <option value="already expired">Already Expired</option>
                        </Input>
                        {validation.touched.filter_mode && validation.errors.filter_mode && (
                            <FormFeedback>{validation.errors.filter_mode}</FormFeedback>
                        )}
                    </FormGroup>

                    {/* Filter Frequency */}
                    <FormGroup>
                        <Label htmlFor="filter_frequency">Filter Frequency</Label>
                        <Input
                            type="number"
                            id="filter_frequency"
                            name="filter_frequency"
                            value={validation.values.filter_frequency}
                            disabled={renewalTrackTrigger.isView || renewalTrackTrigger.isEdit}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={validation.touched.filter_frequency && !!validation.errors.filter_frequency}
                        />
                        {validation.touched.filter_frequency && validation.errors.filter_frequency && (
                            <FormFeedback>{validation.errors.filter_frequency}</FormFeedback>
                        )}
                    </FormGroup>

                    {/* Trigger Frequency */}
                    <FormGroup>
                        <Label htmlFor="trigger_frequency">Trigger Frequency</Label>
                        <Input
                            type="select"
                            id="trigger_frequency"
                            name="trigger_frequency"
                            value={validation.values.trigger_frequency}
                            disabled={renewalTrackTrigger.isView || renewalTrackTrigger.isEdit}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={validation.touched.trigger_frequency && !!validation.errors.trigger_frequency}
                        >
                            <option value="">Select</option>
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                        </Input>
                        {validation.touched.trigger_frequency && validation.errors.trigger_frequency && (
                            <FormFeedback>{validation.errors.trigger_frequency}</FormFeedback>
                        )}
                    </FormGroup>

                    {/* Weekly Trigger Day (Conditional) */}
                    {validation.values.trigger_frequency === 'weekly' && (
                        <FormGroup>
                            <Label htmlFor="weekly_trigger_day">Weekly Trigger Day</Label>
                            <Input
                                type="select"
                                id="weekly_trigger_day"
                                name="weekly_trigger_day"
                                value={validation.values.weekly_trigger_day}
                                disabled={renewalTrackTrigger.isView || renewalTrackTrigger.isEdit}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                invalid={
                                    validation.touched.weekly_trigger_day && !!validation.errors.weekly_trigger_day
                                }
                            >
                                <option value="">Select</option>
                                <option value="Monday">Monday</option>
                                <option value="Tuesday">Tuesday</option>
                                <option value="Wednesday">Wednesday</option>
                                <option value="Thursday">Thursday</option>
                                <option value="Friday">Friday</option>
                                <option value="Saturday">Saturday</option>
                                <option value="Sunday">Sunday</option>
                            </Input>
                            {validation.touched.weekly_trigger_day && validation.errors.weekly_trigger_day && (
                                <FormFeedback>{validation.errors.weekly_trigger_day}</FormFeedback>
                            )}
                        </FormGroup>
                    )}

                    {/* Monthly Trigger Date (Conditional) */}
                    {validation.values.trigger_frequency === 'monthly' && (
                        <FormGroup>
                            <Label htmlFor="monthly_trigger_date">Monthly Trigger Date<span className='text-danger fst-italic fw-lighter' style={{fontSize:"10px"}}>(Enter between 1 to 30)</span></Label>
                            <Input
                                type="number"
                                id="monthly_trigger_date"
                                name="monthly_trigger_date"
                                value={validation.values.monthly_trigger_date}
                                disabled={renewalTrackTrigger.isView || renewalTrackTrigger.isEdit}
                                onChange={validation.handleChange}
                                onBlur={validation.handleBlur}
                                min="1"
                                max="31"
                                invalid={
                                    validation.touched.monthly_trigger_date &&
                                    !!validation.errors.monthly_trigger_date
                                }
                            />
                            {validation.touched.monthly_trigger_date && validation.errors.monthly_trigger_date && (
                                <FormFeedback>{validation.errors.monthly_trigger_date}</FormFeedback>
                            )}
                        </FormGroup>
                    )}

                    {/* Start Date */}
                    <FormGroup>
                        <Label htmlFor="start_date">Start Date</Label>
                        {/* <Input
                            type="date"
                            id="start_date"
                            name="start_date"
                            value={validation.values.start_date}
                            disabled={renewalTrackTrigger.isView || renewalTrackTrigger.isEdit}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={validation.touched.start_date && !!validation.errors.start_date}
                        /> */}

                        <DatePicker
                         type="date"
                         id="start_date"
                         name="start_date"
                         value={validation.values.start_date}
                         disabled={renewalTrackTrigger.isView || renewalTrackTrigger.isEdit}
                         onChange={validation.handleChange}
                         onBlur={validation.handleBlur}
                         invalid={validation.touched.start_date && !!validation.errors.start_date}/>
                        {validation.errors.start_date && (
                            <p className='text-danger' style={{fontSize:"10px"}}>{validation.errors.start_date}</p>
                        )}
                    </FormGroup>

                    {/* Next Trigger Date */}
                    {/* <FormGroup>
                        <Label htmlFor="next_trigger_date">Next Trigger Date</Label>
                        <Input
                            type="date"
                            id="next_trigger_date"
                            name="next_trigger_date"
                            value={validation.values.next_trigger_date}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={validation.touched.next_trigger_date && !!validation.errors.next_trigger_date}
                        />
                        {validation.touched.next_trigger_date && validation.errors.next_trigger_date && (
                            <FormFeedback>{validation.errors.next_trigger_date}</FormFeedback>
                        )}
                    </FormGroup> */}

                    <FormGroup className="w-100">
                        <Label htmlFor="status">Status</Label>
                        <Input
                            type="select"
                            name="status"
                            className='form-group w-100 d-block'
                            value={validation.values.status}
                            disabled={renewalTrackTrigger.isView}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={validation.touched.status && !!validation.errors.status}
                        >
                            <option value="">Select</option>
                            <option value={0}>Inactive</option>
                            <option value={1}>Active</option>
                        </Input>
                        {validation.touched.status && validation.errors.status && (
                            <FormFeedback>{validation.errors.status}</FormFeedback>
                        )}
                    </FormGroup>

                    {/* Form Actions */}
                    <FormGroup className="d-flex justify-content-between">
                        <Button 
                        type="button" 
                        color="danger" 
                        disabled={loading}
                        onClick={closeTriggerForm}>
                            Cancel
                        </Button>
                        {!renewalTrackTrigger.isView && 
                        <Button 
                        type="submit" 
                        color="primary"
                        disabled={loading}>
                           {loading ? "Loading": "Submit"}
                        </Button>}
                    </FormGroup>
                </Form>
            </ModalBody>
        </Modal>
    )
}

export default TriggerForm
